import React, { useEffect, useState } from "react";
import { Bell, Warning, X, CheckCircle, Boat, Package } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * ShortfallBell
 * -------------
 * Shows the current count of unresolved Supervisor shortfall alerts. Clicking
 * expands a dropdown with each alert and a one-tap Resolve button.
 * Only renders for admin / manager / supervisor roles.
 */
export default function ShortfallBell() {
    const { user } = useAuth();
    const canSee = user && ["admin", "manager", "supervisor"].includes(user.role);
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ items: [], unresolved_count: 0 });
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (!canSee) return;
        setLoading(true);
        try {
            const { data } = await api.get("/supervisor/alerts", { params: { unresolved_only: true, limit: 20 } });
            setData(data || { items: [], unresolved_count: 0 });
        } catch (e) {
            // Fail silently in the header — admins shouldn't be blocked by a
            // broken bell. Only surface a toast on explicit resolve action.
            // eslint-disable-next-line no-console
            console.warn("shortfall bell load failed:", formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);
    useEffect(() => {
        if (!canSee) return;
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSee]);

    const resolve = async (id) => {
        try {
            await api.post(`/supervisor/alerts/${id}/resolve`);
            toast.success("Alert resolved");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    if (!canSee) return null;

    const count = data.unresolved_count || 0;
    const critical = (data.items || []).some((a) => a.severity === "critical");

    return (
        <div className="relative" data-testid="shortfall-bell-root">
            <button
                onClick={() => setOpen((s) => !s)}
                className="relative inline-flex items-center justify-center w-9 h-9 rounded-sm bg-white/10 hover:bg-white/20 text-white"
                title={count > 0 ? `${count} stock-shortfall alert${count > 1 ? "s" : ""}` : "No active shortfall alerts"}
                data-testid="shortfall-bell-btn"
            >
                <Bell size={18} weight={count > 0 ? "fill" : "regular"} />
                {count > 0 && (
                    <span
                        className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black flex items-center justify-center text-white shadow ${critical ? "bg-[var(--grindrod-red)] animate-pulse" : "bg-amber-500"}`}
                        data-testid="shortfall-bell-count"
                    >
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>

            {open && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                    <div
                        className="absolute right-0 mt-2 w-[22rem] max-w-[90vw] max-h-[70vh] overflow-auto bg-white text-[var(--grindrod-navy)] rounded-md shadow-2xl border border-slate-200 z-40"
                        data-testid="shortfall-bell-dropdown"
                    >
                        <div className="px-3 py-2.5 bg-[var(--grindrod-navy)] text-white flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Warning size={14} weight="fill" />
                                <span className="font-display font-bold uppercase tracking-wide text-xs">Stock-shortfall alerts</span>
                            </div>
                            <button onClick={() => setOpen(false)} className="opacity-70 hover:opacity-100">
                                <X size={14} />
                            </button>
                        </div>
                        {loading && <div className="p-4 text-xs text-slate-400">Loading…</div>}
                        {!loading && (data.items || []).length === 0 && (
                            <div className="p-6 text-center text-slate-400 text-xs italic flex flex-col items-center gap-2">
                                <CheckCircle size={22} weight="duotone" />
                                No open shortfalls. Every allocated client has enough stock-on-ground for the next vessel.
                            </div>
                        )}
                        <div className="divide-y divide-slate-100">
                            {(data.items || []).map((a) => (
                                <AlertRow key={a.id} a={a} onResolve={() => resolve(a.id)} />
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}


function AlertRow({ a, onResolve }) {
    const sev = a.severity || "warning";
    const bg = sev === "critical" ? "#FEE2E2" : "#FEF3C7";
    const fg = sev === "critical" ? "#B91C1C" : "#B45309";
    const when = a.etb ? new Date(a.etb).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
    return (
        <div className="px-3 py-3 hover:bg-slate-50" data-testid={`shortfall-alert-${a.id}`}>
            <div className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: fg }} />
                <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate">{a.title}</div>
                    <div className="flex items-center flex-wrap gap-1.5 mt-1">
                        <span className="text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded-sm" style={{ color: fg, background: bg }}>
                            {sev}
                        </span>
                        <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                            <Package size={10} /> Pile {a.pile_id}
                        </span>
                        <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                            <Boat size={10} /> {a.vessel_name || "—"}
                        </span>
                    </div>
                    <div className="text-[11px] text-slate-600 mt-1 leading-snug">{a.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1 tabular">ETB {when}</div>
                    <div className="flex justify-end mt-2">
                        <button
                            onClick={onResolve}
                            className="text-[10px] px-2 py-1 rounded-sm bg-[var(--grindrod-navy)] text-white font-semibold uppercase tracking-wide hover:opacity-90"
                            data-testid={`shortfall-resolve-${a.id}`}
                        >
                            <CheckCircle size={11} weight="bold" className="inline mr-1" /> Resolve
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
